import {
  PumpSuspendReason,
  PumpSuspendState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import type { PumpSuspensionActivity } from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"
import { usePolyglot } from "src/contexts"
import { useReadableTime } from "src/hooks"
import type {
  ReadOnlySubEntry,
  ReadOnlyUserEvent,
} from "src/types"

export type Props = ComponentPropsWithoutRef<"div"> &
  {
    item: ReadOnlyUserEvent | ReadOnlySubEntry;
    activity: PumpSuspensionActivity;
  }

const prefix = "components.activity_summary.message.pump_suspension."

export const PumpSuspensionActivityMessage = forwardRef<HTMLDivElement, Props>(
  function PumpSuspensionActivityMessage(props: Props, ref: Ref<HTMLDivElement>) {
    const {
      activity,
      item,
      ...htmlProps
    } = props

    const polyglot = usePolyglot()
    const endTime = useReadableTime("event_type" in item ? item.ended_at : "")

    const message = useMemo(
      () => {
        if (
          activity.state === PumpSuspendState.Completed &&
          activity.suspend_reason !== PumpSuspendReason.PumpMalfunction
        ) {
          return polyglot.t(
            `${prefix}insulin_resumed_at`,
            { time: endTime },
          )
        }

        switch (activity.suspend_reason) {
          case PumpSuspendReason.Alarm:
            return polyglot.t(`${prefix}alarm`)
          case PumpSuspendReason.PumpMalfunction:
            return polyglot.t(`${prefix}malfunction`)
          case PumpSuspendReason.UserAborted:
            return polyglot.t(`${prefix}stopped_by_user`)
        }

        return null
      },
      [
        activity.state,
        activity.suspend_reason,
        endTime,
        polyglot,
      ],
    )

    if (!message) {
      return null
    }

    return (
      <div
        {...htmlProps}
        ref={ref}
      >
        {message}
      </div>
    )
  },
)
